import { convertToYesNo, isDefined } from '@zorro/common-utils';
import { Badge } from '@zorro/design-system/v2/Badge';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { CsvFileIcon } from '@zorro/shared/assets';
import {
  formatCurrencyEnUs,
  formatDateEnLocale,
} from '@zorro/shared/formatters';
import { oepPaymentMethodLabelConfig } from '@zorro/types';
import { Box, Icon } from '@zorro/web/design-system';

import {
  PlanYearSetupFormFields,
  exportAllowanceModelToCsv,
  renderNullableDateRange,
} from '../../../PlanYearSetupForm/PlanYearSetupUtils';
import { AddEmployerStepType } from '../../AddEmployerStep/AddEmployerUtils';
import { ReviewCard } from '../ReviewCard';

type Props = {
  isFinalizationMode: boolean;
  isAllowanceModelRequired: boolean;
  fields: PlanYearSetupFormFields;
  employerName: string;
  employerId: string;
  isValid: boolean;
  isDon: boolean;
  isLynxPaymentsEnabled?: boolean;
};

export const PlanYearSetupCard = ({
  employerId,
  employerName,
  fields,
  isValid,
  isDon,
  isFinalizationMode,
  isAllowanceModelRequired,
  isLynxPaymentsEnabled = false,
}: Props) => {
  'use no memo';
  const {
    effectiveDates,
    oepDates,
    allowanceModel,
    paymentMethod,
    initialDrawAmount,
    initialDrawDate,
    reserveAmount,
    thresholdAmount,
    isAORChanged,
  } = fields;

  return (
    <ReviewCard
      isDon={isDon}
      isValid={isValid}
      title="Plan year setup"
      employerId={employerId}
      step={AddEmployerStepType.PLAN_YEAR_SETUP}
      items={[
        {
          name: 'Coverage period',
          value: renderNullableDateRange(effectiveDates),
          isRequired: true,
        },
        {
          name: 'Enrollment window',
          toolTipLabel:
            'Put a tentative window if you haven’t finalized it yet; you will be able to change the dates later',
          value: renderNullableDateRange(oepDates),
          isRequired: true,
        },
        {
          name: 'Allowance model',
          value: allowanceModel
            ? !(allowanceModel instanceof File) && (
                <Box
                  onClick={() =>
                    exportAllowanceModelToCsv(allowanceModel, employerName)
                  }
                  w="min-content"
                  style={{ cursor: 'pointer' }}
                >
                  <Icon icon={CsvFileIcon} />
                </Box>
              )
            : !isAllowanceModelRequired && (
                <Tooltip label="Optional for now - employees can't be added until an allowance model is uploaded">
                  <Badge color="yellow">No file uploaded</Badge>
                </Tooltip>
              ),
          isRequired: isAllowanceModelRequired,
        },
        {
          name: 'payments-separator',
          isSeparator: true,
        },
        {
          name: 'payments',
          title: 'Payments:',
        },
        {
          name: 'Payment method',
          value: paymentMethod && oepPaymentMethodLabelConfig[paymentMethod],
          isRequired: isFinalizationMode,
        },
        {
          name: 'Initial draw amount',
          value:
            initialDrawAmount !== undefined &&
            formatCurrencyEnUs(initialDrawAmount),
          isRequired: isFinalizationMode,
        },
        {
          name: 'Initial draw date',
          value:
            initialDrawDate !== undefined &&
            formatDateEnLocale(initialDrawDate),
          isRequired: isLynxPaymentsEnabled && isFinalizationMode,
        },
        {
          name: 'Reserve amount',
          value:
            reserveAmount !== undefined && formatCurrencyEnUs(reserveAmount),
          isRequired: isLynxPaymentsEnabled && isFinalizationMode,
        },
        ...(isLynxPaymentsEnabled
          ? [
              {
                name: 'Threshold amount',
                value:
                  thresholdAmount !== undefined &&
                  formatCurrencyEnUs(thresholdAmount),
                isRequired: isFinalizationMode,
              },
            ]
          : []),
        {
          name: 'agentOfRecord-separator',
          isSeparator: true,
        },
        {
          name: 'agentOfRecord',
          title: 'Agent of record:',
        },
        {
          name: 'Employer is changing vendors and AOR',
          value: isDefined(isAORChanged) && convertToYesNo(isAORChanged),
          span: 8,
        },
      ]}
    />
  );
};
